import React from 'react'
import { Link } from 'react-router-dom'
import { t } from '../i18n'
import { useSurface } from '../Utils/analytics'
import { articleOpened } from '../Utils/article'
import { timeAgo } from '../Utils/time'
import { ChevronRight } from './Icons'
import ShareButton from './ShareButton'
import { SourceAvatar, SourceStack } from './SourceAvatar'
import TopicTag from './TopicTag'

/* Source, time, NOVO and U RAZVOJU (spec 5.2, item 2), across the full
   width of the card, wrapping when it doesn't fit. */
export function CardMeta({ story, isNew }) {
  return (
    <div className="card-meta">
      <SourceAvatar source={story.source} size={20} />
      <Link to={`/izvor/${story.source.slug}`} state={{ entry: 'card' }} className="card-source">
        {story.source.name}
      </Link>
      <span className="card-dot" aria-hidden="true">
        ·
      </span>
      <time dateTime={story.publishedAt}>{timeAgo(story.publishedAt)}</time>
      {isNew && <span className="badge-new">{t('card.new')}</span>}
      {story.isDeveloping && (
        <span className="badge-developing">
          <span className="dev-dot" aria-hidden="true" />
          {t('card.developing')}
        </span>
      )}
    </div>
  )
}

/* The headline links to the portal's article in a new tab (spec 5.2). */
export function ArticleLink({
  story,
  entry = 'card',
  position = null,
  isNew = false,
  className,
  children,
  tabIndex,
  ariaHidden,
}) {
  const surface = useSurface()
  return (
    <a
      href={story.url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      tabIndex={tabIndex}
      aria-hidden={ariaHidden}
      onClick={() => articleOpened({ story, entry, position, isNew, surface })}
    >
      {children}
    </a>
  )
}

export function SourcesButton({ story }) {
  if (story.sourceCount < 2) return <span />
  return (
    <Link
      to={`/prica/${story.id}`}
      state={{ entry: 'sources_button' }}
      className="sources-button"
      aria-label={`${t('card.sources', { count: story.sourceCount })}: ${t('card.openStory')}`}
    >
      <SourceStack sources={story.sourcePreview} size={20} />
      <span>{t('card.sources', { count: story.sourceCount })}</span>
      <ChevronRight size={16} strokeWidth={2.5} />
    </Link>
  )
}

/* A story in a feed (spec 5.2). No pictures: news images belong to agencies
   and photographers (spec 1a), so the source's logo, the topic and the type
   carry the card. `tag` replaces the story's own topic tag (null = none),
   as Moje teme does with the topic that brought the story in. position (from
   1) and entry describe a headline click for analytics. */
export default function StoryCard({
  story,
  isNew = false,
  hideTopic = null,
  tag = undefined,
  position = null,
  entry = 'card',
}) {
  return (
    <article className="story-card">
      {/* A topic page doesn't tag its own stories with itself. */}
      {tag !== undefined ? (
        tag
      ) : (
        <TopicTag topic={story.topic?.slug === hideTopic ? null : story.topic} />
      )}
      <CardMeta story={story} isNew={isNew} />
      <h2 className="card-title">
        <ArticleLink story={story} entry={entry} position={position} isNew={isNew}>
          {story.title}
        </ArticleLink>
      </h2>
      <div className="card-footer">
        <SourcesButton story={story} />
        <ShareButton story={story} />
      </div>
    </article>
  )
}
